import { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import {Music, Plus, Edit2, Trash2, Search, X, Video, Mic} from 'lucide-react'

interface WorshipSong {
  _id: string
  title: string
  artist: string
  type: 'VIDEO' | 'AUDIO'
  videoUrl?: string
  audioUrl?: string
  lyrics?: string
  thumbnailUrl?: string
  recommendedBy?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export default function WorshipManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'ALL' | 'VIDEO' | 'AUDIO'>('ALL')
  const [isCreating, setIsCreating] = useState(false)
  const [editingSong, setEditingSong] = useState<WorshipSong | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    type: 'VIDEO' as 'VIDEO' | 'AUDIO',
    videoUrl: '',
    audioUrl: '',
    lyrics: '',
    thumbnailUrl: '',
    recommendedBy: ''
  })

  const { data: songs, loading, fetchData, createRecord, updateRecord, deleteRecord } = useEntityCRUD<WorshipSong>('worship_songs')
  const { logAction } = useAdminLog()

  useEffect(() => {
    fetchData()
  }, [])

  const filteredSongs = songs.filter(song => {
    const matchesSearch = song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         song.artist.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'ALL' || song.type === filterType
    return matchesSearch && matchesType
  })

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createRecord({
        ...formData,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await logAction('CREATE', 'worship_songs', '', `Louvor criado: ${formData.title}`)
      resetForm()
    } catch (error) {
      console.error('Erro ao criar louvor:', error)
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingSong) return
    try {
      await updateRecord(editingSong._id, {
        ...formData,
        updatedAt: new Date().toISOString()
      })
      await logAction('UPDATE', 'worship_songs', editingSong._id, `Louvor atualizado: ${formData.title}`)
      resetForm()
    } catch (error) {
      console.error('Erro ao atualizar louvor:', error)
    }
  }

  const handleDelete = async (song: WorshipSong) => {
    if (!confirm(`Confirma exclusão do louvor "${song.title}"?`)) return
    try {
      await deleteRecord(song._id)
      await logAction('DELETE', 'worship_songs', song._id, `Louvor excluído: ${song.title}`)
    } catch (error) {
      console.error('Erro ao excluir louvor:', error)
    }
  }

  const handleEdit = (song: WorshipSong) => {
    setEditingSong(song)
    setFormData({
      title: song.title,
      artist: song.artist,
      type: song.type,
      videoUrl: song.videoUrl || '',
      audioUrl: song.audioUrl || '',
      lyrics: song.lyrics || '',
      thumbnailUrl: song.thumbnailUrl || '',
      recommendedBy: song.recommendedBy || ''
    })
    setIsCreating(true)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      artist: '',
      type: 'VIDEO',
      videoUrl: '',
      audioUrl: '',
      lyrics: '',
      thumbnailUrl: '',
      recommendedBy: ''
    })
    setEditingSong(null)
    setIsCreating(false)
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gerenciar Louvores</h1>
            <p className="text-gray-600 mt-1">Biblioteca de músicas de louvor e adoração</p>
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Novo Louvor
          </button>
        </div>

        {/* Create/Edit Form */}
        {isCreating && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingSong ? 'Editar Louvor' : 'Adicionar Novo Louvor'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={editingSong ? handleUpdate : handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Artista *</label>
                  <input
                    type="text"
                    required
                    value={formData.artist}
                    onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'VIDEO' | 'AUDIO' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="VIDEO">Vídeo</option>
                    <option value="AUDIO">Áudio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recomendado por</label>
                  <input
                    type="text"
                    value={formData.recommendedBy}
                    onChange={(e) => setFormData({ ...formData, recommendedBy: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {formData.type === 'VIDEO' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL do Vídeo *</label>
                    <input
                      type="url"
                      required={formData.type === 'VIDEO'}
                      value={formData.videoUrl}
                      onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                {formData.type === 'AUDIO' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL do Áudio *</label>
                    <input
                      type="url"
                      required={formData.type === 'AUDIO'}
                      value={formData.audioUrl}
                      onChange={(e) => setFormData({ ...formData, audioUrl: e.target.value })}
                      placeholder="https://example.com/audio.mp3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL da Thumbnail</label>
                  <input
                    type="url"
                    value={formData.thumbnailUrl}
                    onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Letra</label>
                <textarea
                  rows={6}
                  value={formData.lyrics}
                  onChange={(e) => setFormData({ ...formData, lyrics: e.target.value })}
                  placeholder="Cole a letra da música aqui..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingSong ? 'Atualizar Louvor' : 'Adicionar Louvor'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar louvores por título ou artista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('ALL')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filterType === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setFilterType('VIDEO')}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  filterType === 'VIDEO' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Video size={18} />
                Vídeo
              </button>
              <button
                onClick={() => setFilterType('AUDIO')}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  filterType === 'AUDIO' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Mic size={18} />
                Áudio
              </button>
            </div>
          </div>
        </div>

        {/* Songs Grid */}
        {loading ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center text-gray-500">
            Carregando louvores...
          </div>
        ) : filteredSongs.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center text-gray-500">
            Nenhum louvor encontrado
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSongs.map((song) => (
              <div key={song._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                {song.thumbnailUrl ? (
                  <img src={song.thumbnailUrl} alt={song.title} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Music className="text-white" size={64} />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{song.title}</h3>
                      <p className="text-sm text-gray-600">{song.artist}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {song.type === 'VIDEO' ? (
                        <Video className="text-red-600" size={20} />
                      ) : (
                        <Mic className="text-green-600" size={20} />
                      )}
                    </div>
                  </div>
                  {song.recommendedBy && (
                    <p className="text-xs text-gray-500 mb-3">Recomendado por {song.recommendedBy}</p>
                  )}
                  {song.lyrics && (
                    <p className="text-xs text-gray-500 mb-3 line-clamp-2 italic">{song.lyrics.split('\n')[0]}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(song)}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                    >
                      <Edit2 size={16} />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(song)}
                      className="flex items-center justify-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Louvores</p>
                <p className="text-2xl font-bold text-gray-900">{songs.length}</p>
              </div>
              <Music className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vídeos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {songs.filter(s => s.type === 'VIDEO').length}
                </p>
              </div>
              <Video className="text-red-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Áudios</p>
                <p className="text-2xl font-bold text-gray-900">
                  {songs.filter(s => s.type === 'AUDIO').length}
                </p>
              </div>
              <Mic className="text-green-600" size={32} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
